/**
 * JourneySectionProgress.tsx
 *
 * Grouped journey progress bar. Instead of one dash per page (19 of them, see
 * SegmentedProgressBar), the journey is shown as a handful of NAMED sections —
 * e.g. Your Details → Your Offer → KYC & Banking → Agreement.
 *
 * Visual language (shape carries the meaning, colour only reinforces it, so it
 * still reads in greyscale / for colour-blind users):
 *   - current  → large green SQUIRCLE (rounded-[14px] on a 40px box) with a ring,
 *                showing its section number.
 *   - done     → smaller filled green CIRCLE with a white tick.
 *   - upcoming → smaller outlined circle, still showing its section number.
 * Connectors between nodes turn green once the node on their left is done.
 *
 * Sections come straight from the backend (GET journey state → progress_sections,
 * built in los_app/journey.py from each PageConfig.layout['progress_section']),
 * so grouping is config, not code. This component owns all the derivation: give
 * it the section list plus the current page ID and it figures out which node is
 * active.
 */

import React from 'react';
import { Check } from 'lucide-react';

export interface JourneyProgressSection {
  key: string;
  label: string;
  short_label: string;
  /** Every page in the section, including observer pages — decides which node is active. */
  page_ids: string[];
  /** Only pages that count toward progress. Sent by the backend; unused here. */
  counted_page_ids: string[];
}

interface JourneySectionProgressProps {
  sections: JourneyProgressSection[];
  /** ID of the page currently being shown. */
  currentPageId?: number | string | null;
  /** Show the number inside not-yet-reached nodes. */
  showUpcomingNumbers?: boolean;
  color?: string;
}

const JourneySectionProgress: React.FC<JourneySectionProgressProps> = ({
  sections,
  currentPageId,
  showUpcomingNumbers = true,
  color = '#065a4e', // intellend-green
}) => {
  if (!sections || sections.length === 0) return null;

  // Which section are we in? page_ids (not counted_page_ids) is used deliberately:
  // an auto-fire or polling page must still light up its OWN section rather than
  // falling back to the previous one.
  const pageId = currentPageId != null ? String(currentPageId) : '';
  let activeIndex = sections.findIndex(s => s.page_ids.includes(pageId));
  if (activeIndex < 0) activeIndex = 0; // unknown page (e.g. legacy stage) → first section

  const activeSection = sections[activeIndex];

  // On the very LAST page of the journey everything is finished, so no node should
  // still be "current" — all four become ticked circles. Detected from the data
  // (last page of the last section) rather than hardcoding a page id.
  const lastSection = sections[sections.length - 1];
  const lastPageId = lastSection.page_ids[lastSection.page_ids.length - 1];
  const allDone = !!pageId && pageId === lastPageId;

  // How far through the ACTIVE section are we? Used to part-fill the connector that
  // leaves the active node, so the bar visibly moves on every page within a section
  // instead of only when the section itself changes. The connector is divided
  // equally among that section's pages: page 1 of 3 → 33%, page 2 → 67%, etc.
  const activePagePos = activeSection.page_ids.indexOf(pageId); // -1 when unknown
  const activeFillRatio = activeSection.page_ids.length > 0 && activePagePos >= 0
    ? (activePagePos + 1) / activeSection.page_ids.length
    : 0;

  return (
    <div
      className="w-full"
      role="progressbar"
      aria-valuemin={1}
      aria-valuenow={allDone ? sections.length : activeIndex + 1}
      aria-valuemax={sections.length}
      aria-valuetext={
        allDone
          ? 'All steps completed'
          : `Step ${activeIndex + 1} of ${sections.length}: ${activeSection.label}`
      }
    >
      {/* Nodes + connectors */}
      <div className="flex items-center">
        {sections.map((section, index) => {
          const isDone = allDone || index < activeIndex;
          const isCurrent = !allDone && index === activeIndex;

          return (
            <React.Fragment key={section.key}>
              {index > 0 && (() => {
                // The connector BEFORE this node belongs to the previous section.
                // Fully green once that section is finished; partially green (by page
                // position) while the user is still inside it; grey after that.
                const prevIndex = index - 1;
                const fillPct = allDone || prevIndex < activeIndex
                  ? 100
                  : prevIndex === activeIndex
                    ? activeFillRatio * 100
                    : 0;
                return (
                  <div
                    aria-hidden="true"
                    className="flex-1 h-[2px] mx-1 overflow-hidden rounded-full bg-gray-200"
                  >
                    <div
                      className="h-full rounded-full transition-[width] duration-500 ease-out"
                      style={{ width: `${fillPct}%`, backgroundColor: color }}
                    />
                  </div>
                );
              })()}
              <div
                aria-hidden="true"
                className={`flex shrink-0 items-center justify-center font-bold transition-all duration-500 ease-out ${
                  isCurrent
                    ? 'h-10 w-10 rounded-[14px] text-sm text-white'
                    : 'h-7 w-7 rounded-full text-xs'
                }`}
                style={
                  isCurrent
                    ? { backgroundColor: color, boxShadow: `0 0 0 4px ${color}22` }
                    : isDone
                      ? { backgroundColor: color, color: '#ffffff' }
                      : {
                          backgroundColor: '#ffffff',
                          color: '#9ca3af',
                          border: '2px solid #d1d5db',
                        }
                }
              >
                {isDone ? (
                  <Check className="h-4 w-4" strokeWidth={3} />
                ) : isCurrent || showUpcomingNumbers ? (
                  index + 1
                ) : null}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Labels — one per node, centred under it. The current one is emphasised;
          once everything is done none is, so the row reads as uniformly complete. */}
      <div className="mt-1.5 flex items-start">
        {sections.map((section, index) => {
          const isCurrentLabel = !allDone && index === activeIndex;
          return (
            <div
              key={section.key}
              className={`flex-1 px-0.5 text-center text-xs font-semibold leading-tight ${
                isCurrentLabel
                  ? 'font-bold'
                  : allDone || index < activeIndex
                    ? 'text-gray-500'
                    : 'text-gray-400'
              }`}
              style={isCurrentLabel ? { color } : undefined}
            >
              {section.label}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default JourneySectionProgress;
